import React, { useState, useRef } from 'react';
import "./Askfameo.scss";
import { GoLightBulb } from "react-icons/go";
import { HiOutlineHashtag } from "react-icons/hi";
import { PiMagicWandFill } from "react-icons/pi";
import { IoDiamondOutline } from "react-icons/io5";
import { FaRegCheckCircle } from "react-icons/fa";
import { CiShare2 } from "react-icons/ci";
import { IoReloadOutline } from "react-icons/io5";
import { CiCirclePlus } from "react-icons/ci";
import { MdOutlineMicNone, MdClose } from "react-icons/md";
import { IoMdSend } from "react-icons/io";
import { CiSearch } from "react-icons/ci";
import { IoArrowBack } from "react-icons/io5";
import { FaCopy, FaCheck } from "react-icons/fa6";
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAiQuota } from '../../components/Hooks/useAiQuota';
import { compressImageForAi } from '../../utils/compressImageForAi';
import { cleanCaptionSentence } from '../../utils/cleanCaptionSentence';
import axios from '../../components/Hooks/axios';

const AskFameo = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const incomingState = location?.state || null;
    const { quota, setQuota, history, setHistory, countdownText, isLimitReached } = useAiQuota();

    const [inputPrompt, setInputPrompt] = useState("");
    const [selectedTone, setSelectedTone] = useState("Trendy");
    const [selectedImage, setSelectedImage] = useState(null);

    // Auto-prefill if navigated from post upload flow
    React.useEffect(() => {
        if (incomingState?.fileData) {
            const file = Array.isArray(incomingState.fileData) ? incomingState.fileData[0] : incomingState.fileData;
            if (file instanceof Blob || file instanceof File) {
                setSelectedImage(file);
                toast.info("Post image loaded for AI analysis!");
            }
        }
        if (incomingState?.caption) {
            setInputPrompt(incomingState.caption);
        }
    }, [incomingState]);

    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Current displayed output - zero hardcoded text
    const [currentCaption, setCurrentCaption] = useState("");
    const [currentHashtags, setCurrentHashtags] = useState([]);
    const [availableVariants, setAvailableVariants] = useState([]);
    const [activeVariantIndex, setActiveVariantIndex] = useState(0);

    // Interactive card input modal state
    const [cardModal, setCardModal] = useState({
        isOpen: false,
        type: "", // 'idea', 'hashtags', 'refine', 'premium'
        title: "",
        subtitle: "",
        fieldLabel: "",
        placeholder: "",
        tone: "",
        value: "",
        iconType: "purple",
    });

    const fileInputRef = useRef(null);
    const cardModalFileInputRef = useRef(null);
    const autoGenerateOnImageSelectRef = useRef(false);

    const handleGenerate = async (overridePrompt = null, overrideTone = null, overrideImage = null) => {
        const imageToUse = overrideImage !== null ? overrideImage : selectedImage;
        const textToUse = overridePrompt !== null 
            ? overridePrompt 
            : (inputPrompt || (currentCaption ? "Give me a completely fresh, alternative creative caption" : (imageToUse ? "Analyze this attached image and generate engaging captions with trending hashtags." : "")));
        const toneToUse = overrideTone !== null ? overrideTone : selectedTone;

        if (!textToUse && !imageToUse) {
            toast.info("Please enter a prompt or attach an image.");
            return;
        }

        if (isLimitReached) {
            toast.warn(`Daily AI limit reached (15/15). Resets in ${countdownText}`);
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("prompt", textToUse);
            formData.append("tone", toneToUse);
            formData.append("type", "post");

            // Collect all previously seen and generated captions so backend guarantees non-duplication
            const allPrevious = [
                cleanCaptionSentence(currentCaption),
                ...(availableVariants || []).map((v) => cleanCaptionSentence(v.text)),
                ...(history || []).map((h) => cleanCaptionSentence(h.caption)),
            ].filter(Boolean);
            const uniquePrevious = Array.from(new Set(allPrevious));
            if (uniquePrevious.length > 0) {
                formData.append("previousCaptions", JSON.stringify(uniquePrevious));
            }

            if (imageToUse) {
                const compressed = await compressImageForAi(imageToUse, 512);
                formData.append("image", compressed);
            }

            const { data } = await axios.post("/ask-fameo/generate", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (data?.status && data?.data) {
                const generatedCaptions = data.data.captions || [];
                const rawBest = generatedCaptions[0]?.text || "Caption generated successfully!";
                const bestCaption = cleanCaptionSentence(rawBest);
                const rawTags = data.data.hashtags || [];
                const tags = rawTags.map(t => cleanCaptionSentence(t).replace(/^#*/, '#'));

                setCurrentCaption(`"${bestCaption}"`);
                setCurrentHashtags(tags);
                setAvailableVariants(generatedCaptions);
                setActiveVariantIndex(0);

                if (data.quota) {
                    setQuota(data.quota);
                }

                // Add to local history list
                const newHistoryItem = {
                    prompt: textToUse || "Visual caption",
                    caption: bestCaption,
                    hashtags: tags,
                    createdAt: new Date(),
                };
                setHistory((prev) => [newHistoryItem, ...(prev || [])]);

                toast.success("✨ Caption generated by Fameo AI!");
            } else {
                toast.error(data?.message || "Failed to generate caption.");
            }
        } catch (err) {
            if (err.response?.status === 429) {
                const q = err.response.data.quota;
                if (q) setQuota(q);
                toast.error(err.response.data.message || "Daily AI limit reached!");
            } else {
                toast.error(err.response?.data?.message || "Failed to generate. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const openCardModal = (type) => {
        if (type === "idea") {
            setCardModal({
                isOpen: true,
                type: "idea",
                title: "Turn this idea into a caption",
                subtitle: "Transform concepts into viral content",
                fieldLabel: selectedImage 
                    ? "What is your idea or concept for this image?" 
                    : "What is your idea or concept?",
                placeholder: "e.g. Sunset beach walk with coffee, feeling grateful and adventurous...",
                tone: "Viral & Engaging",
                value: inputPrompt || "",
                iconType: "purple",
            });
        } else if (type === "refine") {
            setCardModal({
                isOpen: true,
                type: "refine",
                title: "Refine my caption",
                subtitle: "Polish and perfect existing drafts",
                fieldLabel: "Enter your draft caption to refine:",
                placeholder: "Paste or type your draft caption here (e.g. went to the beach today had a good time with friends)...",
                tone: "Minimalist",
                value: inputPrompt || "",
                iconType: "indigo",
            });
        } else if (type === "premium") {
            setCardModal({
                isOpen: true,
                type: "premium",
                title: "Make this sound premium",
                subtitle: "Elevate tone for luxury branding",
                fieldLabel: "Enter your caption to elevate to luxury:",
                placeholder: "Paste or type your caption here (e.g. Just arrived at the hotel and the view from the balcony is nice)...",
                tone: "Luxury & Premium",
                value: inputPrompt || "",
                iconType: "violet",
            });
        }
    };

    const handleHashtagsCardClick = () => {
        setSelectedTone("Trendy");
        if (selectedImage) {
            toast.info("✨ Generating captions and hashtags from attached image...");
            handleGenerate(
                "Analyze this attached image and generate engaging captions with trending hashtags.",
                "Trendy",
                selectedImage
            );
        } else {
            toast.info("Please select an image to generate captions and hashtags.");
            autoGenerateOnImageSelectRef.current = true;
            fileInputRef.current?.click();
        }
    };

    const handleCardModalSubmit = (e) => {
        if (e) e.preventDefault();
        const trimmed = (cardModal.value || "").trim();

        if (!trimmed && !selectedImage) {
            toast.info(
                cardModal.type === "idea" 
                    ? "Please enter your idea or attach an image." 
                    : "Please enter your caption or text."
            );
            return;
        }

        let constructedPrompt = "";
        if (cardModal.type === "idea") {
            constructedPrompt = selectedImage
                ? `Analyze the attached image and create a viral, engaging caption based on this concept/idea: "${trimmed}"`
                : `Turn this idea into a viral and engaging caption: "${trimmed}"`;
        } else if (cardModal.type === "refine") {
            constructedPrompt = `Refine, polish, and perfect this draft caption into an engaging, high-impact sentence: "${trimmed}"`;
        } else if (cardModal.type === "premium") {
            constructedPrompt = `Elevate and rewrite this caption with an ultra-premium, sophisticated, luxury branding tone: "${trimmed}"`;
        }

        const chosenTone = cardModal.tone || selectedTone;
        setSelectedTone(chosenTone);
        if (trimmed) setInputPrompt(trimmed);

        // Close modal and trigger generation
        setCardModal((prev) => ({ ...prev, isOpen: false }));
        handleGenerate(constructedPrompt, chosenTone, selectedImage);
    };

    const handleCopy = () => {
        if (!currentCaption) return;
        const fullContent = `${currentCaption.replace(/^"|"$/g, '')}\n\n${currentHashtags.join(" ")}`;
        navigator.clipboard.writeText(fullContent);
        setCopied(true);
        toast.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
    };

    const filteredHistory = (history || []).filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.prompt?.toLowerCase().includes(q) ||
            item.caption?.toLowerCase().includes(q) ||
            item.hashtags?.some((t) => t.toLowerCase().includes(q))
        );
    });

    return (
        <div className="ask-fameo-wrapper">
            <header className="ask-header">
                <div className="back-btn" onClick={() => navigate(-1)}>
                    <IoArrowBack />
                    <span>Fameo-X</span>
                </div>
                <h1>Ask Fameo-X</h1>
                <div className="right-placeholder">
                    {/* Live Quota Pill in Header */}
                    <div style={{
                        background: isLimitReached ? "rgba(239, 68, 68, 0.15)" : "rgba(166, 83, 157, 0.12)",
                        color: isLimitReached ? "#ef4444" : "#a6539d",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        {isLimitReached ? (
                            <span>Resets: {countdownText}</span>
                        ) : (
                            <span>Credits: {quota.remaining} / {quota.limit}</span>
                        )}
                    </div>
                </div>
            </header>

            <div className="main-content">
                <div className="left-section">
                    <div className="prompts-grid">
                        <div
                            className="prompt-card"
                            onClick={() => openCardModal("idea")}
                        >
                            <div className="icon-box purple">
                                <GoLightBulb />
                            </div>
                            <div className="text-content">
                                <h3>Turn this idea into a caption</h3>
                                <p>Transform concepts into viral content</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={handleHashtagsCardClick}
                        >
                            <div className="icon-box blue">
                                <HiOutlineHashtag />
                            </div>
                            <div className="text-content">
                                <h3>Generate captions + hashtags</h3>
                                <p>The full engagement package</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={() => openCardModal("refine")}
                        >
                            <div className="icon-box indigo">
                                <PiMagicWandFill />
                            </div>
                            <div className="text-content">
                                <h3>Refine my caption</h3>
                                <p>Polish and perfect existing drafts</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={() => openCardModal("premium")}
                        >
                            <div className="icon-box violet">
                                <IoDiamondOutline />
                            </div>
                            <div className="text-content">
                                <h3>Make this sound premium</h3>
                                <p>Elevate tone for luxury branding</p>
                            </div>
                        </div>
                    </div>

                    <div className="output-container">
                        <div className="premium-tag">
                            <FaRegCheckCircle />
                            <span>{loading ? "GENERATING WITH GEMINI..." : "Output"}</span>
                        </div>

                        <div className="output-text">
                            <p>
                                {loading
                                    ? "Analyzing prompt and generating creative variations..."
                                    : currentCaption
                                    ? currentCaption
                                    : "Drop your raw thought or prompt below, or pick one of the options above to craft your caption."}
                            </p>
                        </div>

                        <div className="output-hashtags">
                            {!loading && currentHashtags.map((tag, idx) => (
                                <span key={idx}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
                            ))}
                        </div>

                        <div className="output-actions">
                            <button
                                className="regenerate-btn"
                                onClick={() => handleGenerate()}
                                disabled={loading || isLimitReached || (!inputPrompt && !selectedImage && !currentCaption)}
                            >
                                <IoReloadOutline />
                                {loading ? "Crafting..." : "Regenerate"}
                            </button>
                            <button
                                className="share-btn"
                                onClick={handleCopy}
                                title="Copy to clipboard"
                                disabled={!currentCaption}
                            >
                                {copied ? <FaCheck color="#10b981" /> : <FaCopy />}
                            </button>

                            {incomingState && currentCaption && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const cleanCaption = currentCaption.replace(/^"|"$/g, '');
                                        const returnPath = incomingState.fileData !== undefined ? '/UploadPost' : -1;
                                        navigate(returnPath, {
                                            state: {
                                                ...incomingState,
                                                caption: cleanCaption,
                                                tag: currentHashtags.map(t => t.replace(/^#/, '')),
                                            }
                                        });
                                        toast.success("✨ Caption applied to post!");
                                    }}
                                    style={{
                                        background: "linear-gradient(135deg, #a6539d, #c288bc)",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "2rem",
                                        padding: "0.8rem 1.6rem",
                                        fontSize: "1.4rem",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.6rem",
                                        boxShadow: "0 4px 12px rgba(166, 83, 157, 0.3)"
                                    }}
                                >
                                    <FaCheck /> Apply to Post
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="right-section">
                    <h2>Content History</h2>
                    <div className="search-bar">
                        <CiSearch />
                        <input
                            type="text"
                            placeholder="Search captions or keywords..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="history-list">
                        {filteredHistory.length > 0 ? (
                            filteredHistory.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="history-item"
                                    onClick={() => {
                                        setCurrentCaption(`"${item.caption}"`);
                                        setCurrentHashtags(item.hashtags || []);
                                        toast.info("Loaded from history!");
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="item-header">
                                        <span className="tag generated">GENERATED</span>
                                        <span className="timestamp">
                                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "Recent"}
                                        </span>
                                    </div>
                                    <p className="item-preview">{item.caption}</p>
                                    <div className="item-hashtags">
                                        {(item.hashtags || []).slice(0, 3).map((tag, tIdx) => (
                                            <span key={tIdx}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: "center", color: "#8c8fa0", padding: "2rem" }}>
                                {history && history.length > 0 ? "No matches found" : "Your generated captions will appear here."}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <footer className="input-footer">
                {/* Image attachment chip preview */}
                {selectedImage && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#fff",
                        padding: "4px 12px",
                        borderRadius: "16px",
                        marginBottom: "6px",
                        width: "fit-content",
                        fontSize: "0.85rem",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                    }}>
                        <span>Attached Image: {selectedImage.name}</span>
                        <MdClose
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedImage(null)}
                        />
                    </div>
                )}

                <div className="input-container">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                const file = e.target.files[0];
                                setSelectedImage(file);
                                toast.info("Image attached for AI analysis!");
                                if (autoGenerateOnImageSelectRef.current) {
                                    autoGenerateOnImageSelectRef.current = false;
                                    handleGenerate(
                                        "Analyze this attached image and generate engaging captions with trending hashtags.",
                                        "Trendy",
                                        file
                                    );
                                }
                            }
                        }}
                    />
                    <CiCirclePlus
                        className="action-icon"
                        title="Attach image to analyze"
                        onClick={() => fileInputRef.current?.click()}
                    />
                    <input
                        type="text"
                        placeholder={
                            isLimitReached
                                ? `Daily limit reached. Resets in ${countdownText}`
                                : "Drop your raw thought or prompt here..."
                        }
                        value={inputPrompt}
                        onChange={(e) => setInputPrompt(e.target.value)}
                        disabled={loading || isLimitReached}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleGenerate();
                            }
                        }}
                    />
                    <div
                        className="send-btn"
                        onClick={() => handleGenerate()}
                        style={{
                            opacity: (loading || isLimitReached || (!inputPrompt && !selectedImage)) ? 0.5 : 1,
                            cursor: (loading || isLimitReached || (!inputPrompt && !selectedImage)) ? "not-allowed" : "pointer",
                        }}
                    >
                        <IoMdSend />
                    </div>
                </div>
            </footer>

            {/* Interactive Card Prompt Modal */}
            {cardModal.isOpen && (
                <div
                    className="card-input-modal-overlay"
                    onClick={() => setCardModal((prev) => ({ ...prev, isOpen: false }))}
                >
                    <div
                        className="card-input-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <div className={`modal-icon-box ${cardModal.iconType}`}>
                                {cardModal.iconType === "purple" && <GoLightBulb />}
                                {cardModal.iconType === "blue" && <HiOutlineHashtag />}
                                {cardModal.iconType === "indigo" && <PiMagicWandFill />}
                                {cardModal.iconType === "violet" && <IoDiamondOutline />}
                            </div>
                            <div className="modal-title-area">
                                <h2>{cardModal.title}</h2>
                                <p>{cardModal.subtitle}</p>
                            </div>
                            <button
                                type="button"
                                className="modal-close-btn"
                                onClick={() => setCardModal((prev) => ({ ...prev, isOpen: false }))}
                            >
                                <MdClose />
                            </button>
                        </div>

                        <div className="modal-media-section">
                            {selectedImage ? (
                                <div className="media-chip">
                                    <span>Attached: {selectedImage.name}</span>
                                    <MdClose onClick={() => setSelectedImage(null)} title="Remove image" />
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="attach-media-btn"
                                    onClick={() => cardModalFileInputRef.current?.click()}
                                >
                                    <CiCirclePlus size={18} /> Attach Image for Visual Context
                                </button>
                            )}
                            <input
                                type="file"
                                ref={cardModalFileInputRef}
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        setSelectedImage(e.target.files[0]);
                                        toast.info("Image attached for visual context!");
                                    }
                                }}
                            />
                        </div>

                        <form onSubmit={handleCardModalSubmit} className="modal-body">
                            <label>{cardModal.fieldLabel}</label>
                            <textarea
                                rows={4}
                                placeholder={cardModal.placeholder}
                                value={cardModal.value}
                                onChange={(e) =>
                                    setCardModal((prev) => ({ ...prev, value: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleCardModalSubmit();
                                    }
                                }}
                                autoFocus
                            />
                            <div className="input-hint">
                                Press Enter to generate (Shift+Enter for new line)
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setCardModal((prev) => ({ ...prev, isOpen: false }))}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={loading || isLimitReached || (!cardModal.value.trim() && !selectedImage)}
                                >
                                    <PiMagicWandFill /> Generate Caption
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AskFameo;
